import type {
  LngLat,
  MapAdapter,
  MapViewState,
  OsmBackgroundOptions,
  RailingStyle,
  ShapeSurface,
  SurfaceBorder,
  RenderingOptions,
  Shape,
  ShapeRelief,
  ShapeImage,
  ShapeStyle,
  ShapeText,
  ShapeUrlAction,
  TileLayerName,
} from "../adapters/types";
import type { MapFraming } from "./framing";
import type { Command } from "./History";
import type { ShapeStore } from "./ShapeStore";
import type { ShapeWalls } from "./ShapeStore";
import { translateOnGround } from "./groundTranslation";
import type { ShapeRings } from "./shapeRings";

function centroid(points: LngLat[]): LngLat {
  const sum = points.reduce<[number, number]>(
    (acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat],
    [0, 0],
  );
  return [sum[0] / points.length, sum[1] / points.length];
}

/**
 * Un anneau fermé (polygone/rectangle) répète son premier point en dernière position — le
 * moyenner tel quel dans `centroid()` compterait ce point deux fois et biaiserait le résultat.
 * Un cercle ([centre, bord]) ou une ligne (jamais fermée, voir `Shape.coordinates`) n'ont rien à
 * retirer : cette fonction ne retire le dernier point que s'il duplique exactement le premier.
 */
function openRing(points: LngLat[]): LngLat[] {
  if (points.length < 2) return points;
  const [firstLng, firstLat] = points[0]!;
  const [lastLng, lastLat] = points[points.length - 1]!;
  return firstLng === lastLng && firstLat === lastLat ? points.slice(0, -1) : points;
}

export class CreateShapeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shape: Shape,
  ) {}

  do(): void {
    this.store.add(this.shape);
  }

  undo(): void {
    this.store.remove(this.shape.id);
  }
}

export class DeleteShapeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shape: Shape,
  ) {}

  do(): void {
    this.store.remove(this.shape.id);
  }

  undo(): void {
    this.store.add(this.shape);
  }
}

export class MoveVertexCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private vertexIndex: number,
    private from: LngLat,
    private to: LngLat,
  ) {}

  do(): void {
    this.store.moveVertex(this.shapeId, this.vertexIndex, this.to);
  }

  undo(): void {
    this.store.moveVertex(this.shapeId, this.vertexIndex, this.from);
  }
}

interface RotationState extends ShapeRings {
  rotation: number;
}

export class RotateShapeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: RotationState,
    private to: RotationState,
    /** Voir `Shape.shapeKind` — détermine le centre de rotation dans `undo()` ci-dessous. */
    private shapeKind?: Shape["shapeKind"],
  ) {}

  do(): void {
    this.store.rotateShape(this.shapeId, this.to.rotation, this.to.coordinates, this.to.holes);
  }

  /**
   * Applique la rotation inverse à l'état ACTUEL de la forme (lu dans le store), pas un reset
   * absolu à `this.from.coordinates` : sous Annuler/Rétablir normal (toujours strictement LIFO),
   * revient au même résultat, puisque rien d'autre n'a pu s'intercaler entre-temps. Mais ça permet
   * aussi un rollback ciblé (ex. `TokaMapConfig.onSave` rejeté) alors qu'une modification plus fine
   * (ex. un sommet déplacé indépendamment) a déjà été appliquée par-dessus depuis : cette dernière
   * est alors tournée avec le reste plutôt qu'écrasée (voir BESOINS.md §7). Le centre de rotation
   * (centroïde de l'anneau, invariant par rotation) est retrouvé depuis `from`/`to` plutôt que
   * mémorisé séparément — SAUF pour un cercle, dont `coordinates` n'est pas un anneau (juste
   * `[centre, point de bord]`, voir `Shape.coordinates`) : son centre de rotation est directement
   * `coordinates[0]`, pas le centroïde des 2 points (qui donnerait leur milieu).
   *
   * `latCorrection` (`cos(latitude du centre)`) compense le fait qu'un degré de longitude et un
   * degré de latitude ne représentent PAS la même distance réelle dès qu'on s'éloigne de
   * l'équateur — appliquer une matrice de rotation 2D directement sur `[lng, lat]` sans cette
   * correction cisaille la forme en parallélogramme (repéré en pivotant un rectangle réel, à une
   * latitude de ~49°, puis en cliquant Annuler : le rectangle ressortait visiblement déformé,
   * confirmé par un angle mesuré à l'écran très différent de 90°). `do()` n'a pas ce problème : les
   * coordonnées qu'il applique viennent de l'adaptateur, qui pivote en pixels écran (localement
   * isotrope, voir `MapLibreAdapter`'s `rotatePointPx`) avant de reprojeter en lng/lat — seul
   * `undo()` refait lui-même le calcul de rotation, directement en lng/lat, donc lui seul a besoin
   * de cette correction. Les tests existants sur ce fichier utilisent des formes proches de
   * l'équateur (latitude ~0), où `cos(latitude) ≈ 1` masque entièrement le problème.
   */
  undo(): void {
    const current = this.store.getAll().find((s) => s.id === this.shapeId);
    if (!current) return;

    const center =
      this.shapeKind === "circle" ? this.from.coordinates[0]! : centroid(openRing(this.from.coordinates));
    const deltaRad = ((this.from.rotation - this.to.rotation) * Math.PI) / 180;
    const cos = Math.cos(deltaRad);
    const sin = Math.sin(deltaRad);
    const latCorrection = Math.cos((center[1] * Math.PI) / 180);
    const rotate = (ring: LngLat[]) => ring.map(([lng, lat]): LngLat => {
      const dx = (lng - center[0]) * latCorrection;
      const dy = lat - center[1];
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      return [center[0] + rotatedX / latCorrection, center[1] + rotatedY];
    });

    this.store.rotateShape(this.shapeId, this.from.rotation, rotate(current.coordinates), current.holes?.map(rotate));
  }
}

export class MoveShapeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeRings,
    private to: ShapeRings,
  ) {}

  do(): void {
    this.store.moveShape(this.shapeId, this.to.coordinates, this.to.holes);
  }

  /**
   * Retire à l'état ACTUEL de la forme l'écart que ce déplacement a donné à chaque sommet, pas un
   * reset absolu à `this.from` : même raisonnement que `RotateShapeCommand.undo()` (voir son
   * commentaire) — équivalent sous Annuler/Rétablir normal, mais préserve un déplacement de sommet
   * indépendant appliqué par-dessus depuis. L'écart est pris sommet par sommet : en vue inclinée,
   * une translation n'a pas le même écart en lng/lat partout.
   */
  undo(): void {
    const current = this.store.getAll().find((s) => s.id === this.shapeId);
    if (!current) return;

    const anchorTo = this.to.coordinates[0]!;
    const anchorFrom = this.from.coordinates[0]!;
    const untranslate = (ring: LngLat[], to: LngLat[] | undefined, from: LngLat[] | undefined): LngLat[] =>
      to && from && ring.length === to.length
        ? ring.map(([lng, lat], index) => [lng - (to[index]![0] - from[index]![0]), lat - (to[index]![1] - from[index]![1])])
        : translateOnGround(ring, anchorTo, anchorFrom);

    this.store.moveShape(
      this.shapeId,
      untranslate(current.coordinates, this.to.coordinates, this.from.coordinates),
      current.holes?.map((hole, index) => untranslate(hole, this.to.holes?.[index], this.from.holes?.[index])),
    );
  }
}

/**
 * Mise à l'échelle d'une forme (BESOINS.md §13, sélection multiple) : redimensionner le rectangle
 * de sélection étire toutes les formes à l'intérieur, chacune via sa propre `ScaleShapeCommand`
 * (undo par forme, voir BESOINS.md §13).
 *
 * `undo()` est ABSOLU (reset à `from`), pas relatif comme Move/RotateShapeCommand — délibérément :
 * une mise à l'échelle non uniforme (scaleX ≠ scaleY, le cas général en étirant un coin) ne compose
 * PAS simplement sous rotation, contrairement à une translation ou une rotation pure (dont l'angle
 * s'additionne quel que soit le repère de travail). Si le rectangle de sélection ou la carte
 * elle-même est tourné(e) au moment du redimensionnement, exprimer "l'inverse" comme une simple
 * mise à l'échelle par (1/scaleX, 1/scaleY) autour d'une origine, directement en lng/lat, serait
 * incorrect — pas seulement moins composable. Un reset absolu reste, lui, toujours correct : même
 * limite assumée que `MoveVertexCommand.undo()` (qui ne compose qu'avec des sommets *autres* que
 * le sien, jamais avec lui-même).
 */
export class ScaleShapeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeRings,
    private to: ShapeRings,
  ) {}

  do(): void {
    this.store.setRings(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setRings(this.shapeId, this.from);
  }
}

/**
 * Convertit une forme entre polygone et ligne ouverte, en bloc (`shapeKind`/`coordinates`/`style`) —
 * "Couper" un polygone à un sommet (devient une ligne, voir `MapLibreAdapter.handleCutShapeAtVertex`)
 * ou rejoindre les 2 extrémités d'une ligne par glisser (devient un polygone fermé). `undo()` est
 * ABSOLU (reset à `from`), comme `ScaleShapeCommand`/`SetShapeStyleCommand` : rien à composer avec
 * une autre édition entre-temps, un reset direct est toujours correct.
 */
export class ConvertShapeKindCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeRings & { shapeKind: Shape["shapeKind"]; style: ShapeStyle | undefined },
    private to: ShapeRings & { shapeKind: Shape["shapeKind"]; style: ShapeStyle | undefined },
  ) {}

  do(): void {
    this.store.setShapeKindAndCoordinates(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeKindAndCoordinates(this.shapeId, this.from);
  }
}

/** Ouvertures et blocage par les murs d'une forme (outil « ouverture ») — `undo()` absolu. */
export class SetShapeWallsCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeWalls,
    private to: ShapeWalls,
  ) {}

  do(): void {
    this.store.setShapeWalls(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeWalls(this.shapeId, this.from);
  }
}

/**
 * Regroupe plusieurs `Command` en une seule entrée `History` (sélection multiple, BESOINS.md §13) :
 * `do()`/`undo()` appliquent chaque commande enfant dans l'ordre/l'ordre inverse. Poussée comme une
 * SEULE entrée via `history.execute(...)`, ce qui rend tout le groupe annulable/rétablissable en un
 * seul geste (`history.undo()` défait toutes les formes du groupe d'un coup) — contrairement à
 * l'ancien comportement (une `Command` par forme, voir BESOINS.md §13 historique) qui rendait
 * chaque forme annulable indépendamment.
 *
 * `undo()` déroule les commandes enfants en ordre INVERSE de `do()` : sans effet réel ici puisque
 * chaque commande enfant porte sur une forme différente (aucune dépendance entre elles), mais c'est
 * l'ordre correct pour une pile de commandes composées en général (dernière appliquée, première
 * défaite) — cohérent avec le patron `Command` du reste de ce fichier.
 */
export class BatchCommand implements Command {
  /** `store` : ses notifications sont regroupées en une seule pour tout le lot (voir `ShapeStore.batch`). */
  constructor(private commands: Command[], private store?: ShapeStore) {}

  do(): void {
    this.run(() => {
      for (const command of this.commands) command.do();
    });
  }

  undo(): void {
    this.run(() => {
      for (let i = this.commands.length - 1; i >= 0; i--) this.commands[i]!.undo();
    });
  }

  private run(fn: () => void): void {
    if (this.store) this.store.batch(fn);
    else fn();
  }
}

/**
 * Change la couleur de fond/bordure d'une forme par son ID (`Shape.style`) — typiquement déclenché
 * depuis la console (voir `TokaMapInstance.commands`/`setShapeStyle`), pas depuis un geste
 * utilisateur sur la carte (pas de `MapAdapterEvent` dédié).
 *
 * `undo()` est ABSOLU (reset à `from`), comme `ScaleShapeCommand` : un style n'a pas de notion de
 * delta à composer avec une autre modification entre-temps, un reset direct est toujours correct.
 */
export class SetShapeStyleCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeStyle | undefined,
    private to: ShapeStyle | undefined,
  ) {}

  do(): void {
    this.store.setShapeStyle(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeStyle(this.shapeId, this.from);
  }
}

/**
 * Change le style de survol d'une forme par son ID (`Shape.hoverStyle`) — mêmes principes que
 * `SetShapeStyleCommand` (déclenché depuis la console via `TokaMapInstance.setShapeHoverStyle`,
 * `undo()` absolu).
 */
export class SetShapeHoverStyleCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeStyle | undefined,
    private to: ShapeStyle | undefined,
  ) {}

  do(): void {
    this.store.setShapeHoverStyle(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeHoverStyle(this.shapeId, this.from);
  }
}

/**
 * Change l'image d'une forme par son ID (`Shape.image`) — mêmes principes que
 * `SetShapeStyleCommand` (déclenché depuis la console via `TokaMapInstance.setShapeImage`,
 * `undo()` absolu).
 */
export class SetShapeImageCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeImage | undefined,
    private to: ShapeImage | undefined,
  ) {}

  do(): void {
    this.store.setShapeImage(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeImage(this.shapeId, this.from);
  }
}

/**
 * Change le texte affiché au centre d'une forme par son ID (`Shape.text`) — REMPLACE le texte
 * courant (pas de fusion partielle comme `SetShapeStyleCommand` : `ShapeText.text` est obligatoire,
 * mêmes principes que `SetShapeImageCommand`, déclenché depuis la console via
 * `TokaMapInstance.setShapeText`, `undo()` absolu).
 */
export class SetShapeTextCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeText | undefined,
    private to: ShapeText | undefined,
  ) {}

  do(): void {
    this.store.setShapeText(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeText(this.shapeId, this.from);
  }
}

/**
 * Change l'action d'une forme par son ID (`Shape.action`) — REMPLACE la valeur courante (scalaire,
 * pas d'objet à fusionner, contrairement à `SetShapeStyleCommand`), mêmes principes que
 * `SetShapeImageCommand` (déclenché depuis la console via `TokaMapInstance.setShapeAction`,
 * `undo()` absolu).
 */
export class SetShapeActionCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: string | undefined,
    private to: string | undefined,
  ) {}

  do(): void {
    this.store.setShapeAction(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeAction(this.shapeId, this.from);
  }
}

/**
 * Change `Shape.useActionIcon` d'une forme par son ID (affiche ou non une icône signalant que la
 * forme déclenche une action au clic) — REMPLACE entièrement la valeur courante, mêmes principes
 * que `SetShapeActionCommand` (déclenché depuis la console via
 * `TokaMapInstance.setShapeUseActionIcon`, `undo()` absolu).
 */
export class SetShapeUseActionIconCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: boolean | undefined,
    private to: boolean | undefined,
  ) {}

  do(): void {
    this.store.setShapeUseActionIcon(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeUseActionIcon(this.shapeId, this.from);
  }
}

/**
 * Change `Shape.locked` d'une forme par son ID (verrouille/déverrouille — voir `Shape.locked`) —
 * REMPLACE entièrement la valeur courante, mêmes principes que `SetShapeUseActionIconCommand`
 * (déclenché depuis le bouton cadenas de la barre d'outils flottante — voir
 * `MapLibreAdapter.createHoverToolbarElement` — ou directement via
 * `TokaMapInstance.setShapeLocked`, `undo()` absolu).
 */
export class SetShapeLockedCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: boolean | undefined,
    private to: boolean | undefined,
  ) {}

  do(): void {
    this.store.setShapeLocked(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeLocked(this.shapeId, this.from);
  }
}

/**
 * Lie/délie une forme à un thème (`Shape.themeId` — voir `ShapeTheme`) : REMPLACE entièrement la
 * valeur courante, mêmes principes que `SetShapeLockedCommand` (déclenché via
 * `TokaMapInstance.setShapeThemeId`, `undo()` absolu). Ne touche jamais `style`/`hoverStyle` de la
 * forme : c'est bien le seul lien qui change, pas son apparence propre.
 */
export class SetShapeThemeIdCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: string | undefined,
    private to: string | undefined,
  ) {}

  do(): void {
    this.store.setShapeThemeId(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeThemeId(this.shapeId, this.from);
  }
}

/**
 * Change `Shape.walkable` d'une forme par son ID (fait/défait son appartenance au réseau
 * traversable par le pathfinding — voir `core/pathfinding.ts`) — REMPLACE entièrement la valeur
 * courante, mêmes principes que `SetShapeLockedCommand` (déclenché via
 * `TokaMapInstance.setShapeWalkable`, `undo()` absolu).
 */
/** Change `Shape.badge` (voir `TokaMapInstance.setShapeBadge`), `undo()` absolu. */
export class SetShapeBadgeCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["badge"],
    private to: Shape["badge"],
  ) {}

  do(): void {
    this.store.setShapeBadge(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeBadge(this.shapeId, this.from);
  }
}

export class SetShapeWalkableCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: boolean | undefined,
    private to: boolean | undefined,
  ) {}

  do(): void {
    this.store.setShapeWalkable(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeWalkable(this.shapeId, this.from);
  }
}

export class SetShapeDecorCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: { position: LngLat; decor: Shape["decor"] },
    private to: { position: LngLat; decor: Shape["decor"] },
  ) {}

  do(): void {
    this.store.setShapeDecor(this.shapeId, this.to.position, this.to.decor);
  }

  undo(): void {
    this.store.setShapeDecor(this.shapeId, this.from.position, this.from.decor);
  }
}

export class SetShapeHeightCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["height"],
    private to: Shape["height"],
  ) {}

  do(): void {
    this.store.setShapeHeight(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeHeight(this.shapeId, this.from);
  }
}

export class SetShapeDepthCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["depth"],
    private to: Shape["depth"],
  ) {}

  do(): void {
    this.store.setShapeDepth(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeDepth(this.shapeId, this.from);
  }
}

export class SetShapeRailingCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: RailingStyle | undefined,
    private to: RailingStyle | undefined,
  ) {}

  do(): void {
    this.store.setShapeRailing(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeRailing(this.shapeId, this.from);
  }
}

export class SetShapeSurfaceCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeSurface | undefined,
    private to: ShapeSurface | undefined,
  ) {}

  do(): void {
    this.store.setShapeSurface(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeSurface(this.shapeId, this.from);
  }
}

export class SetShapeSurfaceBorderCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: SurfaceBorder | undefined,
    private to: SurfaceBorder | undefined,
  ) {}

  do(): void {
    this.store.setShapeSurfaceBorder(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeSurfaceBorder(this.shapeId, this.from);
  }
}

export class SetShapeWaterCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: boolean | undefined,
    private to: boolean | undefined,
  ) {}

  do(): void {
    this.store.setShapeWater(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeWater(this.shapeId, this.from);
  }
}

export class SetShapeReliefCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeRelief,
    private to: ShapeRelief,
  ) {}

  do(): void {
    this.store.setShapeRelief(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeRelief(this.shapeId, this.from);
  }
}

export class SetShapeElevationCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["elevation"],
    private to: Shape["elevation"],
  ) {}

  do(): void {
    this.store.setShapeElevation(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeElevation(this.shapeId, this.from);
  }
}

export class SetShapeHoverHeightCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["hoverHeight"],
    private to: Shape["hoverHeight"],
  ) {}

  do(): void {
    this.store.setShapeHoverHeight(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeHoverHeight(this.shapeId, this.from);
  }
}

export class SetShapeHoverElevationCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["hoverElevation"],
    private to: Shape["hoverElevation"],
  ) {}

  do(): void {
    this.store.setShapeHoverElevation(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeHoverElevation(this.shapeId, this.from);
  }
}

export class SetShapeVolumeOpacityCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["volumeOpacity"],
    private to: Shape["volumeOpacity"],
  ) {}

  do(): void {
    this.store.setShapeVolumeOpacity(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeVolumeOpacity(this.shapeId, this.from);
  }
}

export class SetShapeMinZoomOffsetCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["minZoomOffset"],
    private to: Shape["minZoomOffset"],
  ) {}

  do(): void {
    this.store.setShapeMinZoomOffset(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeMinZoomOffset(this.shapeId, this.from);
  }
}

export class SetShapeGroundCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["ground"],
    private to: Shape["ground"],
  ) {}

  do(): void {
    this.store.setShapeGround(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeGround(this.shapeId, this.from);
  }
}

/**
 * Change `Shape.pathPriority` d'une forme par son ID (marque/démarque un "chemin"/"mur attractif"
 * pour `core/pathfinding.ts`) — REMPLACE entièrement la valeur courante, mêmes principes que
 * `SetShapeWalkableCommand` (déclenché via `TokaMapInstance.setShapePathPriority`, `undo()` absolu).
 */
export class SetShapePathPriorityCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: Shape["pathPriority"],
    private to: Shape["pathPriority"],
  ) {}

  do(): void {
    this.store.setShapePathPriority(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapePathPriority(this.shapeId, this.from);
  }
}

/**
 * Change le calque d'une forme par son ID (`Shape.layer`, z-order — voir `ShapeStore.getAll`) —
 * REMPLACE entièrement la valeur courante, mêmes principes que `SetShapeLockedCommand` (déclenché
 * depuis "Monter"/"Descendre" du menu contextuel, voir `TokaMapInstance.raiseShapeLayer`/
 * `lowerShapeLayer`, `undo()` absolu).
 */
export class SetShapeLayerCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: number | undefined,
    private to: number | undefined,
  ) {}

  do(): void {
    this.store.setShapeLayer(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeLayer(this.shapeId, this.from);
  }
}

/**
 * Change la charge métier opaque d'une forme par son ID (`Shape.entity`) — REMPLACE entièrement la
 * valeur courante (jamais interprétée par `@tokamap/core`, voir `Shape.entity`), mêmes principes que
 * `SetShapeActionCommand` (déclenché depuis la console via `TokaMapInstance.setShapeEntity`,
 * `undo()` absolu).
 */
export class SetShapeEntityCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: unknown,
    private to: unknown,
  ) {}

  do(): void {
    this.store.setShapeEntity(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeEntity(this.shapeId, this.from);
  }
}

/**
 * Change la configuration de redirection d'une forme par son ID (`Shape.urlAction`) — mêmes
 * principes que `SetShapeTextCommand` (déclenché depuis la console via
 * `TokaMapInstance.setShapeUrlAction`, `undo()` absolu).
 */
export class SetShapeUrlActionCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeUrlAction | undefined,
    private to: ShapeUrlAction | undefined,
  ) {}

  do(): void {
    this.store.setShapeUrlAction(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeUrlAction(this.shapeId, this.from);
  }
}

/**
 * Change le document ouvert au clic sur une forme (`Shape.targetDocumentId`) — mêmes principes que
 * `SetShapeUrlActionCommand` (`undo()` absolu, déclenché depuis `TokaMapInstance
 * .setShapeTargetDocumentId`).
 */
export class SetShapeTargetDocumentIdCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: string | undefined,
    private to: string | undefined,
  ) {}

  do(): void {
    this.store.setShapeTargetDocumentId(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeTargetDocumentId(this.shapeId, this.from);
  }
}

/**
 * Change le texte de survol d'une forme par son ID (`Shape.hoverText`) — mêmes principes que
 * `SetShapeTextCommand` (déclenché depuis la console via `TokaMapInstance.setShapeHoverText`,
 * `undo()` absolu).
 */
export class SetShapeHoverTextCommand implements Command {
  constructor(
    private store: ShapeStore,
    private shapeId: string,
    private from: ShapeText | undefined,
    private to: ShapeText | undefined,
  ) {}

  do(): void {
    this.store.setShapeHoverText(this.shapeId, this.to);
  }

  undo(): void {
    this.store.setShapeHoverText(this.shapeId, this.from);
  }
}

/** `MapViewState` vit dans `adapters/types.ts` (aux côtés de `LngLat`) plutôt qu'ici — c'est aussi
 * le type porté par `MapAdapterEvent`'s `"map-view:changed"`, émis par `MapLibreAdapter` lui-même
 * (`adapters/types.ts` ne peut pas dépendre de ce fichier-ci, qui dépend déjà de lui). Ré-exporté
 * tel quel pour ne rien changer aux imports existants (`import type { MapViewState } from
 * "./core/commands"`). */
export type { MapViewState } from "../adapters/types";

/**
 * Instantané complet de la "configuration" globale de la carte (une seule entité, pas une par forme
 * — voir BESOINS.md §7.1) : la vue de caméra par défaut ET le style par défaut appliqué aux
 * nouvelles formes dessinées. Regroupés dans un seul état plutôt que deux entités suivies
 * séparément, parce que c'est ainsi qu'ils vivent dans le JSON source (`configurations`, voir
 * `jsonLoader`) : une modification de l'un des deux envoie l'état COMPLET à
 * `TokaMapConfig.onSaveConfiguration` (voir `ConfigurationSaveTracker`), exactement comme une
 * modification d'un seul champ d'une forme (`setShapeStyle`) envoie le `Shape` complet à `onSave`.
 *
 * `defaultShapeStyle` est optionnel (voir `MapAdapter.getDefaultShapeStyle`) : pas de style
 * explicite par défaut tant qu'aucun n'a été configuré. Pas de notion de style de survol par défaut
 * ici (`Shape.hoverStyle` reste un champ par-forme uniquement, voir `adapters/types.ts`).
 *
 * `tileLayerName` (voir `TileLayerName`) est, lui, toujours renseigné (jamais optionnel) : comme
 * `view`, `MapAdapter.getTileLayerName()` a toujours une valeur courante concrète (`"unset"` par
 * défaut), contrairement à `defaultShapeStyle` qui peut rester `undefined`.
 *
 * `formatVersion` (voir `LoadedData.formatVersion`) reflète, lui aussi, `configurations.formatVersion`
 * du document source — optionnel comme `defaultShapeStyle` (pas `tileLayerName`) : contrairement à
 * `mapAdapter.getTileLayerName()`, rien dans `MapAdapter` ne porte de version de format courante à
 * lire à tout moment, seulement ce qui a été chargé (ou pas, si `loadInitialData`/`loadJson` ne sont
 * pas utilisés — voir `createTokaMap.ts`'s `getConfigurationState`).
 *
 * `geolocation` (voir BESOINS.md §15/`MapAdapter.getGeolocationEnabled`) est, comme `tileLayerName`,
 * toujours renseigné (jamais optionnel, `false` par défaut) — undoable/persisté via
 * `onSaveConfiguration` comme le reste de cette configuration, même mécanisme que `tileLayerName`.
 */
export interface TokaMapConfigurationState {
  view: MapViewState;
  defaultShapeStyle?: ShapeStyle;
  tileLayerName: TileLayerName;
  /** Optionnel comme `defaultShapeStyle` (pas comme `tileLayerName`) : son absence vaut "tous les
   * défauts", voir `OsmBackgroundOptions`. */
  osmOptions?: OsmBackgroundOptions;
  /** Optionnel comme `osmOptions` : son absence vaut "tous les défauts", voir `RenderingOptions`. */
  renderingOptions?: RenderingOptions;
  /**
   * Couleur de fond du conteneur DOM de la carte (voir `MapAdapter.getBackgroundColor`) — `undefined`
   * par défaut, comme `defaultShapeStyle` (aucune couleur explicite tant qu'aucune n'a été
   * configurée), contrairement à `tileLayerName` (toujours renseigné).
   */
  backgroundColor?: string;
  geolocation: boolean;
  /**
   * Autorise l'inclinaison 3D de la caméra (voir `MapAdapter.getAllowPitch`/
   * `MapLibreAdapterOptions.allowPitch`) — comme `tileLayerName`/`geolocation`, toujours renseigné
   * (jamais optionnel, `false` par défaut), undoable/persisté via `onSaveConfiguration` de la même
   * façon.
   */
  allowPitch: boolean;
  framing?: MapFraming;
  formatVersion?: number;
}

/** Type d'adapter minimal requis pour appliquer une `TokaMapConfigurationState` — voir
 * `applyConfigurationState`. */
export type ConfigurationAdapter = Pick<
  MapAdapter,
  | "setCenter"
  | "setZoom"
  | "setZoomBounds"
  | "setRotation"
  | "setDefaultShapeStyle"
  | "setTileLayerName"
  | "setOsmOptions"
  | "setRenderingOptions"
  | "setBackgroundColor"
  | "setGeolocationEnabled"
  | "setAllowPitch"
  | "setFraming"
>;

/**
 * Applique une `TokaMapConfigurationState` telle quelle sur `mapAdapter` — extrait de
 * `SetConfigurationCommand` pour être réutilisable par `ConfigurationSaveTracker` (rollback direct
 * vers `confirmedConfiguration` sans passer par une `Command`/son `undo()`, voir BESOINS.md §7.1).
 */
export function applyConfigurationState(mapAdapter: ConfigurationAdapter, state: TokaMapConfigurationState): void {
  mapAdapter.setCenter(state.view.center);
  mapAdapter.setZoomBounds(state.view.zoomMin, state.view.zoomMax);
  mapAdapter.setZoom(state.view.zoom);
  mapAdapter.setRotation(state.view.rotationDeg);
  mapAdapter.setDefaultShapeStyle(state.defaultShapeStyle);
  mapAdapter.setTileLayerName(state.tileLayerName);
  mapAdapter.setOsmOptions(state.osmOptions);
  mapAdapter.setRenderingOptions(state.renderingOptions);
  mapAdapter.setBackgroundColor(state.backgroundColor);
  mapAdapter.setGeolocationEnabled(state.geolocation);
  mapAdapter.setAllowPitch(state.allowPitch);
  mapAdapter.setFraming(state.framing);
}

/**
 * Change la configuration globale de la carte (vue de caméra ET/OU style par défaut des nouvelles
 * formes dessinées) — typiquement déclenché depuis la console (voir
 * `TokaMapInstance.setMapView`/`setDefaultShapeStyle`), pas depuis un geste utilisateur sur la carte
 * (pas de `MapAdapterEvent` dédié). Contrairement aux autres Command de ce fichier, ne passe pas par
 * `ShapeStore` : `mapAdapter` en tient lieu, la configuration n'étant pas une `Shape`.
 *
 * `undo()` est ABSOLU (reset à `from`), comme `SetShapeStyleCommand` : une configuration n'a pas de
 * notion de delta à composer avec une autre modification entre-temps, un reset direct est toujours
 * correct.
 */
export class SetConfigurationCommand implements Command {
  constructor(
    private mapAdapter: ConfigurationAdapter,
    private from: TokaMapConfigurationState,
    private to: TokaMapConfigurationState,
  ) {}

  do(): void {
    applyConfigurationState(this.mapAdapter, this.to);
  }

  undo(): void {
    applyConfigurationState(this.mapAdapter, this.from);
  }
}
